// src/types/business.ts - Configuration mise à jour avec inventaires physiques

import { InventoryAuditConfiguration } from './inventory-audit';
import { DEFAULT_PACKAGING_UNITS } from '../utils/productPackaging';
import { TaxConfiguration, DEFAULT_TAX_CONFIGURATION } from './tax';
import type { ReceiptFormat } from './invoice';
import type { LoyaltyPassProgramConfig } from './loyaltyPass';
import { Employee } from './entities';
import i18n from '../i18n';

export type BusinessType = 
  | 'beauty_salon'
  | 'bakery' 
  | 'electromenager'       // NOUVEAU : Électroménager
  | 'retail_store'
  | 'restaurant'
  | 'garage'
  | 'prosthesis_clinic'    // NOUVEAU : Cabinet Prosthèses
  | 'custom';

export interface BusinessTypeConfig {
  id: BusinessType;
  name: string;
  description: string;
  // ✅ SUPPRIMÉ : modules - Les features sont maintenant gérées uniquement via les permissions granulaires
  // ✅ SUPPRIMÉ : defaultRoles - Les rôles sont maintenant gérés via les profils de rôle personnalisés
  // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  // ✅ SUPPRIMÉ : terminology - Système de terminologie supprimé pour simplifier. On utilise des termes fixes partout.
}

export const BUSINESS_TYPE_CONFIGS: Record<BusinessType, BusinessTypeConfig> = {
  beauty_salon: {
    id: 'beauty_salon',
    name: 'Institut de beauté',
    description: 'Salon de coiffure, esthétique, onglerie',
    // ✅ SUPPRIMÉ : defaultRoles - Les rôles sont maintenant gérés via les profils de rôle personnalisés
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },
  
  bakery: {
    id: 'bakery',
    name: 'Boulangerie',
    description: 'Boulangerie, pâtisserie',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },
  
  electromenager: {
    id: 'electromenager',
    name: 'Électroménager',
    description: 'Vente d\'électroménager et appareils électroniques',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },

  restaurant: {
    id: 'restaurant',
    name: 'Restaurant',
    description: 'Restaurant, café',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },
  
  retail_store: {
    id: 'retail_store',
    name: 'Magasin de détail',
    description: 'Vêtements, accessoires, articles divers',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },

  garage: {
    id: 'garage',
    name: 'Garage automobile',
    description: 'Réparation, entretien automobile',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },
  
  prosthesis_clinic: {
    id: 'prosthesis_clinic',
    name: 'Cabinet Prosthèses',
    description: 'Cabinet de prothèses (dentaires, auditives, orthopédiques, etc.)',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  },
  
  custom: {
    id: 'custom',
    name: 'Personnalisé',
    description: 'Configuration sur mesure',
    // ✅ SUPPRIMÉ : defaultServiceCategories et defaultProductCategories - Les catégories sont dans des collections Firestore
  }
};

// ✅ NOUVEAU : Interface pour les catégories de dépenses personnalisées
export interface ExpenseCategoryConfig {
  id: string;              // ID unique (généré)
  name: string;            // Nom de la catégorie
  color: string;           // Couleur Ant Design (blue, red, green, etc.)
  isDefault: boolean;      // Si c'est une catégorie système (par défaut)
  isActive: boolean;       // Actif/inactif
  expenseSize?: 'small' | 'large';  // ✅ NOUVEAU : Type de dépense (petite/grosse)
  createdAt: Date;
  updatedAt?: Date;
}

export interface MessagesClientsTemplatesConfiguration {
  generic?: string;
  services?: string;
  products?: string;
  mixed?: string;
}

/** Mode de sortie stock à la vente (optionnel, défaut pos_only). */
export type SalesSiteMode = 'pos_only' | 'any_site';

export interface BusinessConfiguration {
  loyalty: {
    enabled: boolean;        // ✅ NOUVEAU : Flag d'activation de la cagnotte
    bonusPercentage: number;
    minimumUsage: number;
  };
  marketing: {
    maxActiveCampaigns?: number;
  };
  messagesClients?: {
    delayMinutes: number;
    antiSpamHours: number;
    requireOptIn: boolean;
    channel: 'whatsapp';
    templates?: MessagesClientsTemplatesConfiguration;
  };
  inventory: {
    enabled: boolean;
    lowStockThreshold: number;
    /** Si true (défaut), le prix d'achat (costPrice) est recalculé à chaque approvisionnement (WAC). Si false, on conserve le prix manuel. */
    recalculateCostOnProvisioning?: boolean;
    /** Si true, le business utilise plusieurs lieux (magasin + boutique) : stock par site, transferts, appro et ventes rattachés à un site. Géré par Super Admin. */
    multiSitesEnabled?: boolean;
    /**
     * Mode de rattachement des ventes aux lieux.
     * - `pos_only` (défaut) : vente uniquement depuis le point de vente (profil boutique / magasin+boutique).
     * - `any_site` : vente depuis tout lieu actif (distribution / vente terrain). Géré par Super Admin.
     */
    salesSiteMode?: SalesSiteMode;
    /**
     * Si true, active le type d'opération « dépôt-vente » (créance à la remise, stock sort à la création).
     * Orthogonal à `salesSiteMode` : boutique seule, magasin+boutique, ou multi-dépôts.
     * Requiert la feature contrat `paymentPlans` côté app.
     */
    depotVenteEnabled?: boolean;
  };
  // ✅ NOUVEAU : Configuration TVA
  tax: TaxConfiguration;
  // ✅ NOUVELLE : Configuration des inventaires physiques
  inventoryAudit?: InventoryAuditConfiguration;
  // ✅ NOUVEAU : Configuration des catégories de dépenses
  // Note: Les catégories système (salary, delivery) sont toujours disponibles
  expenses?: {
    categories: ExpenseCategoryConfig[]; // Catégories personnalisées uniquement
  };
  // Module Activité : rapports d'activité
  // Note: validationMode et weeklyDeadlineDay sont maintenant configurés par employé (Employee.activityReportConfig)
  activityReport?: {
    /** Types d'activité : liste de libellés (optionnel), utilisés dans les rapports d'activité */
    activityTypes?: string[];
  };
  // Module Activité : suivi commercial (pipeline / opportunités). L'accès aux features est géré par les rôles.
  salesActivity?: {
    defaultPipelineId?: string;
    /** Types de clients : liste de libellés (optionnel), utilisés sur clients et opportunités */
    clientTypes?: string[];
    /** Catégories d'opportunités : liste de libellés (optionnel) */
    opportunityCategories?: string[];
  };
  /** Configuration catalogue produits */
  products?: {
    /** Unités de conditionnement paramétrables (ex. g, kg, L, pièce) */
    packagingUnits?: string[];
  };
  /** Configuration documents de vente (ticket / facture A4) */
  invoicing?: InvoicingConfiguration;
  /** Module Ramassage pressing (marketplace + lien direct). */
  pickup?: {
    marketplaceEnabled: boolean;
    publicSlug: string;
    serviceAreas: Array<{ city: string; districts: string[] }>;
    feeServiceId?: string;
    /** Vitrine publique (marketplace + page /p/:slug). */
    publicProfile?: {
      profilePhotoUrl?: string;
      coverPhotoUrl?: string;
      galleryPhotoUrls?: string[];
      videoUrls?: string[];
      description?: string;
      contactPhone?: string;
      contactWhatsapp?: string;
      contactEmail?: string;
      socialFacebook?: string;
      socialInstagram?: string;
      socialTiktok?: string;
    };
  };
}

export interface InvoicingConfiguration {
  /** Format d'impression ; défaut thermal si absent */
  receiptFormat?: ReceiptFormat;
  /** Afficher cachet et signature entreprise (nécessite feature invoiceStamp) */
  showCompanyStampAndSignature?: boolean;
  /** Ligne d’activité sous le logo (facture A4) */
  documentTagline?: string;
  /** Pied de page centré : mentions légales, contacts */
  footerLegalText?: string;
  /** Bloc libre sous le tableau (conditions, remerciements…) */
  postTableText?: string;
  /** Libellé au-dessus du cachet / signature (défaut « La direction ») */
  signatureLabel?: string;
}

/** Type pour la configuration des rapports d'activité d'un employé */
export interface ActivityReportConfiguration {
  validationMode: 'daily' | 'weekly' | 'monthly';
  weeklyDeadlineDay?:
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';
  /** Heure max de validation (0–23). Défaut 23. */
  deadlineHour?: number;
}

export interface BusinessBranding {
  smsSenderName: string;
  phone: string;
  whatsappNumber: string;
  address: string;
  primaryColor: string;
  secondaryColor: string;
  logo?: string;
  companyStampUrl?: string;
  companySignatureUrl?: string;
  description?: string;
  welcomeMessage?: string;
  footerText?: string;
}

// ✅ Nouvelle couche : features activables par business (contrat)
export type BusinessFeatureKey =
  | 'dashboard'
  | 'pos'
  | 'clients'
  | 'paymentPlans'
  /** Fidélité carte (Pass) — module NG branché backend BonSpot */
  | 'loyaltyPass'
  | 'referents'
  | 'appointments'
  | 'inventory'
  | 'inventoryExternalDispatch'
  | 'inventoryLosses'
  /** Ramassage pressing (demandes publiques + inbox + vente au kg) */
  | 'pickup'
  | 'manufacturing'
  | 'purchasesExpenses'
  | 'expenseCategories'
  | 'expenseNotes'
  | 'licencePayment'
  | 'performanceIndicators'
  | 'accounting'
  | 'marketing'
  | 'messagesClients'
  | 'services'
  /** Tarif au kilo sur les prestations (catalogue + caisse) */
  | 'perKgPricing'
  | 'employeesPayroll'
  /** Rapports d'activité (ce que font les employés) */
  | 'activityReports'
  /** Pipeline commercial / opportunités */
  | 'opportunities'
  /** Configuration des pipelines (Paramètres) — couplée aux opportunités */
  | 'pipelineConfig'
  | 'clientTypes'
  | 'activityTypes'
  | 'opportunityCategories'
  | 'users'
  | 'invoiceStamp'
  /** Packs catalogue (prestations et/ou produits) */
  | 'catalogPacks';

/**
 * Features qui exigent un parent activé (sans auto-activation du parent→enfant).
 * Ex. : Pertes nécessite Stock ; désactiver Stock retire aussi Pertes.
 */
export const FEATURE_REQUIRES_OF: Partial<
  Record<BusinessFeatureKey, BusinessFeatureKey | BusinessFeatureKey[]>
> = {
  inventoryLosses: 'inventory',
  pickup: ['pos', 'clients', 'appointments'],
  perKgPricing: 'services',
  catalogPacks: 'pos',
};

/** Enfants optionnels retirés quand le parent est désactivé. */
export const FEATURE_OPTIONAL_DEPENDENTS_OF: Partial<
  Record<BusinessFeatureKey, BusinessFeatureKey[]>
> = {
  inventory: ['inventoryLosses'],
  pos: ['pickup', 'catalogPacks'],
  clients: ['pickup'],
  appointments: ['pickup'],
  services: ['perKgPricing'],
};

/**
 * Features enfants activées automatiquement avec leur parent.
 */
export const FEATURE_CHILDREN_OF: Partial<Record<BusinessFeatureKey, BusinessFeatureKey[]>> = {
  activityReports: ['activityTypes'],
  opportunities: ['clientTypes', 'opportunityCategories'],
  purchasesExpenses: ['expenseCategories'],
};

/** Parent forcé pour une feature enfant. */
export const FEATURE_PARENT_OF: Partial<Record<BusinessFeatureKey, BusinessFeatureKey>> = {
  activityTypes: 'activityReports',
  clientTypes: 'opportunities',
  opportunityCategories: 'opportunities',
  expenseCategories: 'purchasesExpenses',
};

/**
 * Groupes mutuels : activer l’un active tous les autres ; retirer l’un retire tous.
 * Opportunités ↔ Configuration pipelines.
 */
export const FEATURE_MUTUAL_GROUPS: BusinessFeatureKey[][] = [
  ['opportunities', 'pipelineConfig'],
];

/** Peers mutuels d’une feature (hors elle-même). */
export const getMutualFeaturePeers = (featureKey: BusinessFeatureKey): BusinessFeatureKey[] => {
  for (const group of FEATURE_MUTUAL_GROUPS) {
    if (group.includes(featureKey)) {
      return group.filter((k) => k !== featureKey);
    }
  }
  return [];
};

/** Ancienne feature monolithique → modules distincts (rétrocompat Firestore). */
const LEGACY_ACTIVITY_EXPANSION: BusinessFeatureKey[] = [
  'activityReports',
  'opportunities',
  'pipelineConfig',
];

const KNOWN_FEATURE_KEYS = new Set<string>([
  // populated after BUSINESS_FEATURES — use explicit list to allow filtering before const
  'dashboard',
  'pos',
  'clients',
  'paymentPlans',
  'loyaltyPass',
  'referents',
  'appointments',
  'inventory',
  'inventoryExternalDispatch',
  'inventoryLosses',
  'pickup',
  'manufacturing',
  'purchasesExpenses',
  'expenseCategories',
  'expenseNotes',
  'licencePayment',
  'performanceIndicators',
  'accounting',
  'marketing',
  'messagesClients',
  'services',
  'perKgPricing',
  'employeesPayroll',
  'activityReports',
  'opportunities',
  'pipelineConfig',
  'clientTypes',
  'activityTypes',
  'opportunityCategories',
  'users',
  'invoiceStamp',
  'catalogPacks',
]);

/** Normalise une liste brute (Firestore) : legacy `activity`, ignore `fleet`, etc. */
export const normalizeBusinessFeatureKeys = (
  features: ReadonlyArray<string>
): BusinessFeatureKey[] => {
  const set = new Set<BusinessFeatureKey>();
  for (const raw of features) {
    if (raw === 'activity') {
      LEGACY_ACTIVITY_EXPANSION.forEach((k) => set.add(k));
      continue;
    }
    if (raw === 'fleet' || raw === 'fleet_management') {
      continue;
    }
    if (KNOWN_FEATURE_KEYS.has(raw)) {
      set.add(raw as BusinessFeatureKey);
    }
  }
  return Array.from(set);
};

/** Applique le couplage parent ↔ enfants + groupes mutuels sur une liste de features. */
export const applyFeatureCoupling = (features: ReadonlyArray<string>): BusinessFeatureKey[] => {
  const set = new Set(normalizeBusinessFeatureKeys(features));

  // Groupes mutuels : si un membre est présent, tous le sont
  for (const group of FEATURE_MUTUAL_GROUPS) {
    if (group.some((k) => set.has(k))) {
      group.forEach((k) => set.add(k));
    }
  }

  for (const [child, parent] of Object.entries(FEATURE_PARENT_OF) as Array<
    [BusinessFeatureKey, BusinessFeatureKey]
  >) {
    if (set.has(child) && !set.has(parent)) {
      set.add(parent);
    }
  }

  // Re-appliquer mutuels après ajout de parents (ex. clientTypes → opportunities → pipelineConfig)
  for (const group of FEATURE_MUTUAL_GROUPS) {
    if (group.some((k) => set.has(k))) {
      group.forEach((k) => set.add(k));
    }
  }

  for (const [parent, children] of Object.entries(FEATURE_CHILDREN_OF) as Array<
    [BusinessFeatureKey, BusinessFeatureKey[]]
  >) {
    if (set.has(parent)) {
      children.forEach((child) => set.add(child));
    } else {
      children.forEach((child) => set.delete(child));
    }
  }

  return Array.from(set);
};

export interface BusinessFeatureDefinition {
  key: BusinessFeatureKey;
  label: string;
  description?: string;
}

export const BUSINESS_FEATURES: BusinessFeatureDefinition[] = [
  { key: 'dashboard', label: 'Tableau de bord' },
  { key: 'pos', label: 'Caisse / Point de vente' },
  {
    key: 'catalogPacks',
    label: 'Packs catalogue',
    description:
      'Nécessite la Caisse. Forfaits prestations/produits à prix fixe, vendus en caisse.',
  },
  { key: 'clients', label: 'Clients' },
  { key: 'paymentPlans', label: 'Paiement en plusieurs' },
  {
    key: 'loyaltyPass',
    label: 'Carte de fidélité BonSpot',
    description:
      'Carte de fidélité BonSpot (tampons / récompenses) dans Gestion. Mutex avec la cagnotte FCFA. Backend BonSpot partagé.',
  },
  { key: 'referents', label: 'Référents' },
  { key: 'appointments', label: 'Rendez-vous' },
  { key: 'inventory', label: 'Stock / Inventaire' },
  { key: 'inventoryExternalDispatch', label: 'Stock - Bons externes' },
  {
    key: 'inventoryLosses',
    label: 'Stock - Pertes',
    description: 'Nécessite le module Stock. Déclaration et suivi des pertes / invendus.',
  },
  {
    key: 'pickup',
    label: 'Ramassage',
    description:
      'Nécessite Caisse, Clients, Rendez-vous, et au moins une ville active (Villes & quartiers). Demandes publiques, planification des passages, suivi client.',
  },
  { key: 'manufacturing', label: 'Ordres de fabrication' },
  { key: 'purchasesExpenses', label: 'Achats & Dépenses' },
  {
    key: 'expenseCategories',
    label: 'Types de dépenses',
    description: 'Activé automatiquement avec Achats & Dépenses',
  },
  { key: 'expenseNotes', label: 'Notes de frais' },
  { key: 'licencePayment', label: 'Règlements Nauvatek (espace client)' },
  { key: 'performanceIndicators', label: 'Indicateur de performance' },
  { key: 'accounting', label: 'Comptabilité' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'messagesClients', label: 'Messages Clients' },
  { key: 'services', label: 'Prestations' },
  {
    key: 'perKgPricing',
    label: 'Tarif au kilo',
    description: 'Nécessite Prestations. Mode prix/kg sur le catalogue et saisie kg en caisse.',
  },
  { key: 'employeesPayroll', label: 'Employés & Paies' },
  {
    key: 'activityReports',
    label: 'Rapports d’activité',
    description: 'Suivi de l’activité des employés',
  },
  {
    key: 'opportunities',
    label: 'Opportunités',
    description: 'Toujours lié à Configuration des pipelines',
  },
  {
    key: 'pipelineConfig',
    label: 'Configuration des pipelines',
    description: 'Toujours lié aux Opportunités',
  },
  {
    key: 'clientTypes',
    label: 'Types de clients',
    description: 'Activé automatiquement avec Opportunités',
  },
  {
    key: 'activityTypes',
    label: 'Types d’activité',
    description: 'Activé automatiquement avec Rapports d’activité',
  },
  {
    key: 'opportunityCategories',
    label: 'Catégories d’opportunités',
    description: 'Activé automatiquement avec Opportunités',
  },
  { key: 'users', label: 'Utilisateurs (Super Admin)' },
  {
    key: 'invoiceStamp',
    label: 'Cachet et signature sur factures',
    description: 'Upload et affichage du cachet / signature entreprise sur les documents',
  },
];

export interface Business {
  id: string;
  name: string;
  type: BusinessType;
  isActive: boolean;
  /** Indique si le business est en cours de configuration (verrou pour les utilisateurs finaux) */
  configurationInProgress?: boolean;
  createdAt: Date;
  /** Liste déclarative des features activées pour ce business (contrat). Si undefined ou vide, aucune feature explicite (sauf celles en default-on). */
  activeFeatures?: BusinessFeatureKey[];
  /**
   * Features désactivées explicitement (opt-out des features default-on).
   * Ex. : `licencePayment` est active par défaut ; la mettre ici la désactive.
   */
  disabledFeatures?: BusinessFeatureKey[];
  configuration?: BusinessConfiguration;
  branding?: BusinessBranding;
  /**
   * Id merchant BonSpot (Firebase BS) lié 1:1 à ce business.
   * Renseigné après `provisionMerchantFromNauvatek` / sync bridge.
   */
  bonspotMerchantId?: string;
  /**
   * Programme Fidélité carte (Pass) — source de vérité UI NG.
   * Synchronisé vers merchant BS (loyalty*).
   */
  loyaltyPassProgram?: LoyaltyPassProgramConfig;
}

/** Features activées par défaut pour tous les businesses (sauf opt-out via disabledFeatures). */
export const DEFAULT_ON_BUSINESS_FEATURES: BusinessFeatureKey[] = ['licencePayment'];

// ✅ MISE À JOUR : Configuration par défaut avec inventaires
export const DEFAULT_BUSINESS_CONFIGURATION: BusinessConfiguration = {
  loyalty: {
    enabled: true,           // ✅ NOUVEAU : Activée par défaut pour rétrocompatibilité
    bonusPercentage: 3,
    minimumUsage: 1000,
  },
  marketing: {
    maxActiveCampaigns: 10,
  },
  messagesClients: {
    delayMinutes: 15,
    antiSpamHours: 24,
    requireOptIn: true,
    channel: 'whatsapp',
    // Défauts FR figés pour seed Firestore ; génération runtime → resolveMessagesClientsTemplate (i18n).
    templates: {
      generic: [
        'Bonjour {clientName},',
        'Merci pour votre confiance aujourd’hui.',
        'Votre cagnotte fidélité : {loyaltyBalance} FCFA 💎',
        "Suite à votre transaction, tout s'est bien passé ?",
        'Nous restons disponibles si vous avez besoin de conseils ou d’assistance.',
      ].join('\n'),
      services: [
        'Bonjour {clientName},',
        'Merci pour votre prestation aujourd’hui.',
        'Votre cagnotte fidélité : {loyaltyBalance} FCFA 💎',
        "Nous espérons que tout s'est bien déroulé.",
        'Nous restons disponibles si vous avez la moindre question.',
      ].join('\n'),
      products: [
        'Bonjour {clientName},',
        'Merci pour votre achat aujourd’hui.',
        'Votre cagnotte fidélité : {loyaltyBalance} FCFA 💎',
        "Nous espérons que tout se passe bien avec votre achat.",
        'Nous restons disponibles pour toute aide complémentaire.',
      ].join('\n'),
      mixed: [
        'Bonjour {clientName},',
        'Merci pour votre prestation et vos achats aujourd’hui.',
        'Votre cagnotte fidélité : {loyaltyBalance} FCFA 💎',
        "Nous espérons que l'ensemble de votre transaction vous donne satisfaction.",
        'Nous restons disponibles pour toute question.',
      ].join('\n'),
    },
  },
  inventory: {
    enabled: false,
    lowStockThreshold: 5,
    multiSitesEnabled: false,
    salesSiteMode: 'pos_only',
    depotVenteEnabled: false,
  },
  tax: DEFAULT_TAX_CONFIGURATION,
  // Configuration par défaut des inventaires physiques
  inventoryAudit: {
    significantVarianceThreshold: 5, // 5% d'écart
    significantValueThreshold: 10000, // 10000 FCFA
    requireDoubleCountAboveValue: 50000, // Double comptage au-dessus de 50K
    requireDoubleCountAboveVariance: 10, // Double comptage si > 10% d'écart
    recommendedFrequency: 'annual',
    criticalCategories: [],
    physicalZones: [],
    notifyOnSignificantVariance: true,
    notifyOnCompletionMilestones: true,
  },
  // ✅ NOUVEAU : Configuration par défaut des catégories de dépenses
  expenses: {
    categories: [], // Catégories personnalisées uniquement
  },
  // Module Activité : rapports d'activité (désactivé par défaut, à activer par business)
  // Note: validationMode et weeklyDeadlineDay sont maintenant configurés par employé
  activityReport: {
    activityTypes: [],
  },
  // Module Activité : suivi commercial (accès géré par les rôles)
  salesActivity: {
    defaultPipelineId: undefined,
    clientTypes: [],
    opportunityCategories: [],
  },
  products: {
    packagingUnits: [...DEFAULT_PACKAGING_UNITS],
  },
};

// Branding par défaut (pour les champs optionnels seulement)
export const DEFAULT_BUSINESS_BRANDING_PARTIAL = {
  primaryColor: '#667eea',
  secondaryColor: '#764ba2',
  welcomeMessage: 'Découvrez votre offre exclusive !',
  footerText: 'Cette offre est personnelle et non transférable',
};

// Fonction utilitaire pour obtenir la configuration avec fallback
export const getBusinessConfiguration = (business: Business | null): BusinessConfiguration => {
  return business?.configuration || DEFAULT_BUSINESS_CONFIGURATION;
};

// ✅ NOUVELLE : Fonction pour obtenir la configuration des inventaires
export const getInventoryAuditConfiguration = (business: Business | null): InventoryAuditConfiguration => {
  const config = getBusinessConfiguration(business);
  return config.inventoryAudit || DEFAULT_BUSINESS_CONFIGURATION.inventoryAudit!;
};

// Fonctions utilitaires existantes
export const getInventoryConfiguration = (business: Business | null) => {
  const config = getBusinessConfiguration(business);
  return config.inventory;
};

/** Mode de vente par lieu — défaut pos_only (comportement historique). */
export const getSalesSiteMode = (business: Business | null): SalesSiteMode => {
  const mode = getInventoryConfiguration(business)?.salesSiteMode;
  return mode === 'any_site' ? 'any_site' : 'pos_only';
};

/** True si les ventes peuvent sortir le stock depuis n'importe quel lieu actif. */
export const canSellFromAnySite = (business: Business | null): boolean =>
  getSalesSiteMode(business) === 'any_site';

/** True si le stock doit être géré par lieu (flag multi-sites OU vente depuis tous les lieux). */
export const isMultiSiteStockEnabled = (business: Business | null): boolean =>
  getInventoryConfiguration(business)?.multiSitesEnabled === true || canSellFromAnySite(business);

/**
 * @deprecated Le mode dépôt-vente séparé n’existe plus : une créance accepte un acompte 0.
 * Conservé pour compat config ; équivalent à la feature `paymentPlans`.
 */
export const isDepotVenteEnabled = (business: Business | null): boolean =>
  isFeatureEnabled(business, 'paymentPlans');

/**
 * Flag config brut (Super Admin) — sans garde feature.
 * Préférer `isDepotVenteEnabled` dans l’app métier.
 */
export const isDepotVenteConfigFlagOn = (business: Business | null): boolean =>
  getInventoryConfiguration(business)?.depotVenteEnabled === true;

/**
 * Libellés UX conditionnels : boutique (pos_only) vs dépôt / lieu (any_site).
 * Générique — pas de vocabulaire vertical (pharma, etc.).
 * Traduits via `common.site.labels.*` selon la locale i18n courante.
 */
export const getSalesSiteUiLabels = (business: Business | null) => {
  const t = i18n.getFixedT(i18n.language, 'common');
  const mode = canSellFromAnySite(business) ? 'anySite' : 'posOnly';
  const p = `site.labels.${mode}` as const;
  return {
    siteNoun: t(`${p}.siteNoun`),
    siteNounPlural: t(`${p}.siteNounPlural`),
    salesSite: t(`${p}.salesSite`),
    salesSiteShort: t(`${p}.salesSiteShort`),
    pointOfSaleType: t(`${p}.pointOfSaleType`),
    selectSalesSite: t(`${p}.selectSalesSite`),
    missingSalesSite: t(`${p}.missingSalesSite`),
    stockAtSite: t(`${p}.stockAtSite`),
    filterBySite: t(`${p}.filterBySite`),
    statsBySite: t(`${p}.statsBySite`),
  };
};

/**
 * Vocabulaire unique « Paiement en plusieurs » (ex-plans / créances), pour tous les business.
 * Acompte facultatif (0 = rien maintenant) — plus de mode dépôt-vente séparé.
 * Traduits via `clients.paymentPlans.labels.*`.
 */
export const getReceivablesUiLabels = (_business: Business | null) => {
  const t = i18n.getFixedT(i18n.language, 'clients');
  const p = 'paymentPlans.labels';
  return {
    menuLabel: t(`${p}.menuLabel`),
    pageTitle: t(`${p}.pageTitle`),
    pageSubtitle: t(`${p}.pageSubtitle`),
    emptyList: t(`${p}.emptyList`),
    createFromCta: t(`${p}.createFromCta`),
    createFromHint: t(`${p}.createFromHint`),
    featureOffTitle: t(`${p}.featureOffTitle`),
    featureOffDescription: t(`${p}.featureOffDescription`),
    permissionDenied: t(`${p}.permissionDenied`),
    listCountLabel: t(`${p}.listCountLabel`),
    overdueStat: t(`${p}.overdueStat`),
    deleteConfirm: t(`${p}.deleteConfirm`),
    cancelConfirm: t(`${p}.cancelConfirm`),
    deleteBlocked: t(`${p}.deleteBlocked`),
    archiveReason: t(`${p}.archiveReason`),
    detailTitle: t(`${p}.detailTitle`),
    createDrawerTitle: t(`${p}.createDrawerTitle`),
    createSubmit: t(`${p}.createSubmit`),
    createSuccess: t(`${p}.createSuccess`),
    createError: t(`${p}.createError`),
    notesPlaceholder: t(`${p}.notesPlaceholder`),
    followUpHint: t(`${p}.followUpHint`),
    /** @deprecated alias — même CTA unique */
    cartDepotCta: t(`${p}.cartDepotCta`),
    cartPlanCta: t(`${p}.cartPlanCta`),
    cartDepotHint: t(`${p}.cartDepotHint`),
    cartPlanHint: t(`${p}.cartPlanHint`),
    cartDepotAlertTitle: t(`${p}.cartDepotAlertTitle`),
    cartDepotAlertDescription: t(`${p}.cartDepotAlertDescription`),
    operationTypeLabel: t(`${p}.operationTypeLabel`),
    depotInfoDescription: t(`${p}.depotInfoDescription`),
    planFromCartTitle: t(`${p}.planFromCartTitle`),
    planFromCartDescription: t(`${p}.planFromCartDescription`),
    permissionCreateDenied: t(`${p}.permissionCreateDenied`),
    allOperationsFilter: t(`${p}.allOperationsFilter`),
    allReceivablesFilter: t(`${p}.allReceivablesFilter`),
  };
};

export const isInventoryEnabled = (business: Business | null): boolean => {
  return isFeatureEnabled(business, 'inventory');
};

// ✅ NOUVELLE : Fonction pour vérifier si les inventaires physiques sont activés
export const isInventoryAuditEnabled = (business: Business | null): boolean => {
  // Les inventaires physiques sont disponibles si le module inventory est activé
  return isInventoryEnabled(business);
};

// Module Activité : configuration des rapports d'activité (uniquement activityTypes, maintenant au niveau business)
// Note: validationMode et weeklyDeadlineDay sont maintenant au niveau employé
export const getActivityReportConfiguration = (business: Business | null) => {
  const config = getBusinessConfiguration(business);
  return config.activityReport ?? DEFAULT_BUSINESS_CONFIGURATION.activityReport!;
};

// Module Activité : configuration de validation des rapports d'activité d'un employé
export const getEmployeeActivityReportConfig = (employee: Employee | null): ActivityReportConfiguration | null => {
  if (!employee?.activityReportConfig) {
    return null; // Pas de config = pas de validation requise
  }
  return {
    validationMode: employee.activityReportConfig.validationMode,
    weeklyDeadlineDay: employee.activityReportConfig.weeklyDeadlineDay ?? 'sunday',
    deadlineHour: employee.activityReportConfig.deadlineHour ?? 23,
  };
};

// Module Activité : configuration du suivi commercial (pipeline / opportunités)
export const getSalesActivityConfiguration = (business: Business | null) => {
  const config = getBusinessConfiguration(business);
  return config.salesActivity ?? DEFAULT_BUSINESS_CONFIGURATION.salesActivity!;
};

/** Unités de conditionnement produit du business (liste de libellés) */
export const getPackagingUnits = (business: Business | null): string[] => {
  const config = getBusinessConfiguration(business);
  const raw = config.products?.packagingUnits;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value, index, arr) => Boolean(value) && arr.indexOf(value) === index);
};

/** Types de clients du business (liste de libellés) */
export const getClientTypes = (business: Business | null): string[] => {
  const config = getBusinessConfiguration(business);
  const raw = config.salesActivity?.clientTypes;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value, index, arr) => Boolean(value) && arr.indexOf(value) === index);
};

/** Catégories d'opportunités du business (liste de libellés) */
export const getOpportunityCategories = (business: Business | null): string[] => {
  const config = getBusinessConfiguration(business);
  const raw = config.salesActivity?.opportunityCategories as unknown;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => {
      if (typeof value === 'string') return value.trim();
      if (value && typeof value === 'object') {
        const maybeLabel = (value as { label?: unknown; name?: unknown }).label;
        const maybeName = (value as { label?: unknown; name?: unknown }).name;
        if (typeof maybeLabel === 'string') return maybeLabel.trim();
        if (typeof maybeName === 'string') return maybeName.trim();
      }
      return '';
    })
    .filter((value, index, arr) => Boolean(value) && arr.indexOf(value) === index);
};

/** Types d'activité du business pour les rapports d'activité (liste de libellés) */
export const getActivityTypes = (business: Business | null): string[] => {
  const config = getBusinessConfiguration(business);
  return config.activityReport?.activityTypes ?? [];
};

// Fonction utilitaire pour obtenir le branding avec validation
export const getBusinessBranding = (business: Business | null): BusinessBranding | null => {
  if (!business?.branding) {
    return null;
  }
  
  const branding = business.branding;
  if (!branding.smsSenderName || !branding.phone || !branding.whatsappNumber || !branding.address) {
    console.error('Branding incomplet pour business:', business.id);
    return null;
  }
  
  return {
    ...DEFAULT_BUSINESS_BRANDING_PARTIAL,
    ...branding,
  } as BusinessBranding;
};

// Fonction pour générer automatiquement le nom SMS depuis le nom business
export const generateSMSSenderName = (businessName: string): string => {
  const cleanName = businessName
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/^(LE|LA|LES|THE|INSTITUT|SALON|SPA|CENTRE|RESTAURANT|BOULANGERIE)/, '')
    .substring(0, 11);
  
  return cleanName.length >= 3 ? cleanName : businessName.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 11);
};

// Fonction pour obtenir le nom SMS (OBLIGATOIRE)
export const getBusinessSMSSenderName = (business: Business | null): string => {
  if (business?.branding?.smsSenderName) {
    return business.branding.smsSenderName;
  }
  
  if (business?.name) {
    return generateSMSSenderName(business.name);
  }
  
  throw new Error('Impossible de déterminer le nom SMS : business manquant ou branding incomplet');
};

// Fonctions utilitaires pour accéder aux configurations spécifiques
export const getLoyaltyConfiguration = (business: Business | null) => {
  const config = getBusinessConfiguration(business);
  return config.loyalty;
};

// ✅ NOUVELLE : Fonction pour vérifier si la cagnotte est activée
export const isLoyaltyEnabled = (business: Business | null): boolean => {
  const loyaltyConfig = getLoyaltyConfiguration(business);
  // Par défaut true pour rétrocompatibilité (si enabled n'existe pas)
  return loyaltyConfig.enabled !== false;
};

export const getMarketingConfiguration = (business: Business | null) => {
  const config = getBusinessConfiguration(business);
  return config.marketing;
};

export const getMessagesClientsConfiguration = (business: Business | null) => {
  const config = getBusinessConfiguration(business);
  return config.messagesClients ?? DEFAULT_BUSINESS_CONFIGURATION.messagesClients!;
};

// Fonctions pour les types de business
/** Stocks / multi-sites : config éditable pour tous les types. */
export const businessSupportsInventoryConfig = (businessType?: BusinessType | null): boolean =>
  !!businessType;

export const getBusinessConfig = (businessType: BusinessType): BusinessTypeConfig => {
  return BUSINESS_TYPE_CONFIGS[businessType];
};

// ✅ SUPPRIMÉ : getBusinessTerminology - Système de terminologie supprimé pour simplifier

// ✅ SUPPRIMÉ : getAvailableRoles - Les rôles sont maintenant gérés via les profils de rôle personnalisés
// Utiliser useRoleProfiles() pour obtenir les profils disponibles pour un business

// ✅ SUPPRIMÉ : getAvailableServiceCategories et getAvailableProductCategories
// Les catégories sont maintenant dans des collections Firestore (serviceCategories et productCategories)
// Utiliser useServiceCategories() et useProductCategories() pour récupérer les catégories

// ✅ Nouvelle API : features par business

/** Retourne la liste des features actives pour un business (contrat + default-on + couplage). */
export const getActiveFeatures = (business: Business | null): BusinessFeatureKey[] => {
  if (!business) {
    // En dehors d'un contexte business (Super Admin pur), on considère tout actif.
    return BUSINESS_FEATURES.map((f) => f.key);
  }
  const disabled = new Set(business.disabledFeatures ?? []);
  const explicit = business.activeFeatures ?? [];

  // Aucune liste explicite : seules les features default-on (non désactivées) sont actives.
  if (explicit.length === 0) {
    return applyFeatureCoupling(DEFAULT_ON_BUSINESS_FEATURES.filter((key) => !disabled.has(key)));
  }

  const merged = new Set(normalizeBusinessFeatureKeys(explicit));
  for (const key of DEFAULT_ON_BUSINESS_FEATURES) {
    if (!disabled.has(key)) {
      merged.add(key);
    }
  }
  return applyFeatureCoupling(Array.from(merged));
};

/** Vérifie si une feature est activée pour un business donné. */
export const isFeatureEnabled = (business: Business | null, feature: BusinessFeatureKey): boolean => {
  const active = getActiveFeatures(business);
  return active.includes(feature);
};

/**
 * Calcule le prochain état activeFeatures / disabledFeatures après un toggle Super Admin.
 * Les features default-on utilisent disabledFeatures pour l'opt-out.
 */
export const computeFeatureToggleUpdate = (
  business: Business,
  featureKey: BusinessFeatureKey,
  checked: boolean
): { activeFeatures: BusinessFeatureKey[]; disabledFeatures: BusinessFeatureKey[] } => {
  const isDefaultOn = DEFAULT_ON_BUSINESS_FEATURES.includes(featureKey);
  const currentActive = getActiveFeatures(business);
  const disabled = new Set(business.disabledFeatures ?? []);

  if (isDefaultOn) {
    if (checked) {
      disabled.delete(featureKey);
    } else {
      disabled.add(featureKey);
    }
  }

  let nextActive: BusinessFeatureKey[];
  if (checked) {
    const toAdd = new Set<BusinessFeatureKey>([featureKey, ...getMutualFeaturePeers(featureKey)]);
    const parent = FEATURE_PARENT_OF[featureKey];
    if (parent) {
      toAdd.add(parent);
      getMutualFeaturePeers(parent).forEach((p) => toAdd.add(p));
    }
    const required = FEATURE_REQUIRES_OF[featureKey];
    if (required) {
      const requiredList = Array.isArray(required) ? required : [required];
      requiredList.forEach((req) => {
        toAdd.add(req);
        getMutualFeaturePeers(req).forEach((p) => toAdd.add(p));
      });
    }
    nextActive = Array.from(new Set([...currentActive, ...Array.from(toAdd)]));
  } else {
    // Feature enfant : non désactivable tant que le parent reste actif (couplage).
    if (FEATURE_PARENT_OF[featureKey] && currentActive.includes(FEATURE_PARENT_OF[featureKey]!)) {
      return {
        activeFeatures: applyFeatureCoupling(currentActive),
        disabledFeatures: Array.from(disabled),
      };
    }
    const toRemove = new Set<BusinessFeatureKey>([featureKey, ...getMutualFeaturePeers(featureKey)]);
    toRemove.forEach((key) => {
      const children = FEATURE_CHILDREN_OF[key];
      children?.forEach((child) => toRemove.add(child));
      const optionalDeps = FEATURE_OPTIONAL_DEPENDENTS_OF[key];
      optionalDeps?.forEach((dep) => toRemove.add(dep));
    });
    nextActive = currentActive.filter((k) => !toRemove.has(k));
  }

  nextActive = applyFeatureCoupling(nextActive);

  // Ne pas persister les default-on dans activeFeatures si la liste était vide :
  // elles restent portées par DEFAULT_ON_BUSINESS_FEATURES + disabledFeatures.
  const explicitWasEmpty = !business.activeFeatures || business.activeFeatures.length === 0;
  if (explicitWasEmpty && isDefaultOn && !checked) {
    nextActive = nextActive.filter((k) => !DEFAULT_ON_BUSINESS_FEATURES.includes(k));
  }

  return {
    activeFeatures: nextActive,
    disabledFeatures: Array.from(disabled),
  };
};
